import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import * as actions from "../../../Redux/Actions";
import MainCard from "../../../component/MainCard/MainCard";
import Button from "../../../component/Buttons/Button";
import SearchBar from "../../../component/SearchBar/SearchBar";
import ModalComponents from "../../../component/ModalComponent/ModalComponent";
import {
  showErrorToastMessage,
  showSuccessToastMessage,
} from "../../../component/Toasts/Toasts";
import hanldeUnauthorizedUser from "../../../component/AccessConditions/UnauthorizedUser";
import { icons } from "../../../assets";
import { Link } from "react-router-dom";
import AddOrEditCriticalRole from "./AddOrEditCriticalRole";
import ConfirmDialog from "../ConfirmDialog";
import "../readable-form.scss";

const CriticalRoles = () => {
  const { unAuthorizeUser } = hanldeUnauthorizedUser();
  const dispatch = useDispatch();

  const [roles, setRoles] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState<any>("");
  const [confirmDelete, setConfirmDelete] = useState<any>(null);

  const handleApiCall = () => {
    dispatch(
      actions.getAllCriticalRolesAction((res: any) => {
        if (res?.error) showErrorToastMessage("Couldn't load critical roles. Please retry.");
        else if (res?.data) setRoles(res.data);
      })
    );
  };

  useEffect(() => {
    unAuthorizeUser();
    handleApiCall();
  }, []);

  const handleDelete = (row: any) => {
    dispatch(
      actions.deleteCriticalRoleAction(row._id, (res: any) => {
        setConfirmDelete(null);
        if (res?.error) {
          const msg =
            res?.error?.response?.data?.error?.error_description ||
            "Failed to remove critical role";
          showErrorToastMessage(msg);
        } else {
          showSuccessToastMessage("Critical role removed");
          handleApiCall();
        }
      })
    );
  };

  const incumbentName = (row: any) => {
    const e = row.incumbent;
    if (!e) return "-";
    return [e.candidateName, e.middleName, e.lastname].filter(Boolean).join(" ").trim() || "-";
  };

  const buHeadName = (row: any) => {
    const e = row.buHead;
    if (!e) return "-";
    return [e.candidateName, e.middleName, e.lastname].filter(Boolean).join(" ").trim() || "-";
  };

  const term = searchValue.toLowerCase();
  const filtered = roles.filter(
    (r) =>
      (r.designation?.title || "").toLowerCase().includes(term) ||
      (r.client?.clientName || "").toLowerCase().includes(term) ||
      (r.criticality || "").toLowerCase().includes(term) ||
      (r.riskLevel || "").toLowerCase().includes(term)
  );

  return (
    <MainCard>
      <div className="assessment-heading-button-wrapper">
        <div>
          <h2>Critical Roles</h2>
        </div>
        <div className="btn-wrapper">
          <Button
            title="+ Identify Critical Role"
            onClick={() => {
              setEditData("");
              setShowModal(true);
            }}
            className="add-employee-btn"
          />
        </div>
      </div>

      <div className="mt-2">
        <div className="assessment-searchbar-wrapper mx-2 mt-3">
          <SearchBar
            filter={searchValue}
            setFilter={setSearchValue}
            onSearchClicked={() => {}}
          />
        </div>

        {filtered.length > 0 ? (
          <div className="table table-responsive">
            <table className="table-main table table-sm mt-3">
              <thead className="table-head">
                <tr className="table-head-row">
                  <th className="table-head-row" scope="col">Client</th>
                  <th className="table-head-row" scope="col">Designation</th>
                  <th className="table-head-row" scope="col">Tier</th>
                  <th className="table-head-row" scope="col">Criticality</th>
                  <th className="table-head-row" scope="col">Risk Level</th>
                  <th className="table-head-row" scope="col">Incumbent</th>
                  <th className="table-head-row" scope="col">BU Head</th>
                  <th className="table-head-row" scope="col">Successors</th>
                  <th className="table-head-row" scope="col">Reason</th>
                  <th className="table-head-row" scope="col">Action</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {filtered.map((r) => (
                  <tr className="table-body-row" key={r._id}>
                    <td>{r.client?.clientName || "-"}</td>
                    <td>
                      {/* opens the seat's successor pool — same pattern as Project Repository */}
                      <Link to={`/succession-planning/critical-roles/${r._id}`}>
                        <span className="seat-link">{r.designation?.title || "-"}</span>
                      </Link>
                    </td>
                    <td>{r.tier || "-"}</td>
                    <td>{r.criticality || "-"}</td>
                    <td>{r.riskLevel || "-"}</td>
                    <td>{incumbentName(r)}</td>
                    <td>{buHeadName(r)}</td>
                    <td>{r.successorCount ?? 0}</td>
                    <td>{r.reason || "-"}</td>
                    <td>
                      <div className="action-btn seat-actions">
                        <img
                          title="Edit"
                          src={icons.edit_icon}
                          alt="edit"
                          className="edit-icon"
                          onClick={() => {
                            setEditData(r);
                            setShowModal(true);
                          }}
                        />
                        <img
                          title="Remove"
                          src={icons.delete_icon}
                          alt="delete"
                          className="edit-icon"
                          onClick={() => setConfirmDelete(r)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="employeedata mt-2">No critical roles marked yet</p>
        )}
      </div>

      <ModalComponents
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setEditData("");
        }}
        title={editData ? "Edit Critical Role" : "Mark Critical Role"}
        modalBody={
          <AddOrEditCriticalRole
            editData={editData}
            setShowModal={setShowModal}
            setEditData={setEditData}
            handleApiCall={handleApiCall}
          />
        }
        className="modal-md"
      />

      <ModalComponents
        show={!!confirmDelete}
        onHide={() => setConfirmDelete(null)}
        title="Remove Critical Role"
        modalBody={
          confirmDelete ? (
            <ConfirmDialog
              message={`Remove "${confirmDelete.designation?.title}"${
                confirmDelete.client?.clientName ? ` at ${confirmDelete.client.clientName}` : ""
              } as a critical role?`}
              detail="Its mapped successors and their development plans will be removed with it."
              confirmLabel="Remove"
              onConfirm={() => handleDelete(confirmDelete)}
              onCancel={() => setConfirmDelete(null)}
            />
          ) : null
        }
        className="modal-md"
      />
    </MainCard>
  );
};

export default CriticalRoles;
